import {
  CircularProgress,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core'
import { useApp } from 'contexts/App/appContext'
import { useFlow } from 'contexts/Flow/flowContext'
import { Property } from 'contexts/Flow/types'
import { useForm } from 'contexts/Form/formContext'
import { useToast } from 'contexts/ToastContext'
import { useCallback, useEffect, useState } from 'react'
import { ChoicesTitle } from 'components/MetadataBlocksDrawerEditor/styles'
import { BotRule, listBotRules } from 'services/FollowUpService'

export function FollowUpEntries({
  entries,
}: Readonly<{ entries: Property[] }>) {
  const { state: stateFlow } = useFlow()
  const { state, updateForm } = useForm()
  const { dispatch: dispatchApp } = useApp()
  const { toastNotification: toast } = useToast()
  const { dataForm } = state

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [followUps, setFollowUps] = useState<BotRule[]>()
  const [selectedFollowUp, setSelectedFollowUp] = useState('')

  const recoverSelectedFollowUp = useCallback(
    (BotRules: BotRule[]) => {
      if (!dataForm || !dataForm.followUp) return

      const selectedFollowUp = BotRules.find(
        (followUp) => followUp.name === dataForm?.followUp,
      )

      if (selectedFollowUp) {
        setSelectedFollowUp(selectedFollowUp.name)
      } else {
        console.warn('Selected follow up not found', dataForm?.followUp)

        updateForm({ ...dataForm, followUp: undefined })

        toast({
          type: 'warning',
          message: 'O Follow Up selecionado anteriormente não foi encontrado.',
        })
      }
    },
    // Toast notification causing infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataForm],
  )

  const getFollowUps = useCallback(
    async (botName: string) => {
      try {
        setIsLoading(true)

        const followUps = await listBotRules(botName, dispatchApp)

        if (!followUps.Success) {
          toast({
            type: 'warning',
            message:
              'Não foi possível buscar a lista de Follow Ups. Tente novamente mais tarde.',
          })

          return
        }

        setFollowUps(followUps.Data.items)

        if (dataForm?.followUp) {
          recoverSelectedFollowUp(followUps.Data.items)
        }
      } catch (error) {
        console.error('Failed to fetch follow up bot rules', error)

        toast({
          type: 'warning',
          message:
            'Não foi possível buscar a lista de Follow Ups. Tente novamente mais tarde.',
        })
      } finally {
        setIsLoading(false)
      }
    },

    // Toast notification causing infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatchApp, recoverSelectedFollowUp],
  )

  const handleChangeFollowUp = (newValue: string) => {
    if (!dataForm) return

    setSelectedFollowUp(newValue)
    updateForm({ ...dataForm, followUp: newValue })
  }

  const renderSelectContent = () => {
    if (isLoading) {
      return (
        <MenuItem disabled value="">
          Carregando...
        </MenuItem>
      )
    }

    if (!followUps) {
      return (
        <MenuItem disabled value="">
          Nenhum Follow Up encontrado
        </MenuItem>
      )
    }

    return followUps.map((followUp) => (
      <MenuItem
        key={followUp.name}
        value={followUp.name}
        disabled={!followUp.active}
      >
        {followUp.name}
      </MenuItem>
    ))
  }

  useEffect(() => {
    if (stateFlow.botName) {
      getFollowUps(stateFlow.botName)
    }
  }, [getFollowUps, stateFlow.botName])

  const renderSelectEntry = () => (
    <div style={{ marginBottom: 16 }}>
      <ChoicesTitle>
        <Typography>{entries[0].label}</Typography>
      </ChoicesTitle>

      <FormControl
        variant="outlined"
        fullWidth
        error={!!state.formErrors?.bubble}
      >
        <Select
          id="follow-up-select"
          value={selectedFollowUp}
          onChange={(e) => handleChangeFollowUp(String(e.target.value))}
          required={entries[0].required}
          maxRows={entries[0].maxLength}
          displayEmpty
        >
          {renderSelectContent()}
        </Select>

        {!!state.formErrors?.followUp && (
          <FormHelperText>{state.formErrors.followUp}</FormHelperText>
        )}
      </FormControl>
    </div>
  )

  return <>{isLoading ? <CircularProgress size={28} /> : renderSelectEntry()}</>
}
